<template>
  <div class="mc-portal">
    <PortalTopBar role="admin" />

    <div class="pt-container">
      <div class="pt-head">
        <div>
          <div class="pt-kicker">Admin</div>
          <h1 class="pt-title">Products</h1>
          <p class="pt-sub">The modules you sell to cities. Active products are the ones your team can offer today.</p>
        </div>
        <div class="head-actions">
          <button v-if="missingStarters.length" type="button" class="pt-btn pt-btn-outline" :disabled="seeding" @click="addStarters">
            <SiteIcon name="spark" :size="17" /> {{ seeding ? "Adding…" : `Add ${missingStarters.length} starter ${missingStarters.length === 1 ? "product" : "products"}` }}
          </button>
          <button type="button" class="pt-btn pt-btn-primary" @click="openCreate"><SiteIcon name="plus" :size="17" /> New product</button>
        </div>
      </div>

      <div class="pt-kpis">
        <div class="pt-kpi"><div class="pt-kpi-label">Active</div><div class="pt-kpi-value">{{ count("active") }}</div><div class="pt-kpi-meta">Offered to cities now</div></div>
        <div class="pt-kpi brass"><div class="pt-kpi-label">Paused</div><div class="pt-kpi-value">{{ count("paused") }}</div><div class="pt-kpi-meta">Drafts and coming soon</div></div>
        <div class="pt-kpi navy"><div class="pt-kpi-label">Archived</div><div class="pt-kpi-value">{{ count("archived") }}</div><div class="pt-kpi-meta">No longer offered</div></div>
        <div class="pt-kpi"><div class="pt-kpi-label">Last change</div><div class="pt-kpi-value small">{{ lastUpdated ? since(lastUpdated) : "—" }}</div><div class="pt-kpi-meta">{{ lastUpdated ? prettyTs(lastUpdated) : "Nothing yet" }}</div></div>
      </div>

      <section class="pt-card">
        <div class="pt-card-head">
          <h2 class="pt-card-title"><SiteIcon name="grid" :size="20" /> All products <span class="pt-count">{{ filtered.length }}</span></h2>
          <div class="filters">
            <label class="pt-search">
              <SiteIcon name="search" :size="16" />
              <input v-model="search" type="text" placeholder="Search products" aria-label="Search products" />
            </label>
            <div class="pt-tabs" role="tablist">
              <button v-for="s in statusTabs" :key="s.value" type="button" class="pt-tab" :class="{ active: statusFilter === s.value }" @click="statusFilter = s.value">{{ s.label }}</button>
            </div>
          </div>
        </div>

        <div v-if="loading"><div v-for="n in 4" :key="n" class="pt-skeleton"></div></div>

        <div v-else-if="loadError" class="pt-notice error"><SiteIcon name="alert" :size="17" /><span>{{ loadError }}</span></div>

        <div v-else-if="!filtered.length" class="pt-empty">
          <SiteIcon name="grid" :size="40" />
          <h3>{{ products.length ? "Nothing matches" : "No products yet" }}</h3>
          <p v-if="products.length">Try a different search or status.</p>
          <p v-else>Add the starter products, or click <b>New product</b> to make your own.</p>
        </div>

        <div v-else class="pt-table">
          <div class="pt-thead cols"><span>Product</span><span>Status</span><span>Updated</span><span></span></div>
          <div v-for="p in filtered" :key="p.id" class="pt-tr cols" @click="openRow(p)">
            <span class="name-cell">
              <span class="mark" :class="markTone(p.name)">{{ initials(p.name) }}</span>
              <span class="name-text">
                <b class="truncate">{{ p.name || "Untitled product" }}</b>
                <span class="truncate pt-muted">{{ p.summary || "No summary yet" }}</span>
              </span>
            </span>
            <span><span class="pt-badge" :class="statusTone(p.status)">{{ statusLabel(p.status) }}</span></span>
            <span class="updated">
              <span>{{ prettyTs(p.updatedAt) }}</span>
              <span class="pt-hint">{{ since(p.updatedAt) }}</span>
            </span>
            <span class="actions" @click.stop>
              <button v-if="hasWorkspace" type="button" class="pt-icon-btn" title="Open workspace" aria-label="Open workspace" @click="openWorkspace(p)"><SiteIcon name="arrowUpRight" :size="16" /></button>
              <button type="button" class="pt-icon-btn" title="Edit" aria-label="Edit" @click="edit(p)"><SiteIcon name="pencil" :size="16" /></button>
              <template v-if="confirmId === p.id">
                <button type="button" class="pt-btn pt-btn-danger pt-btn-sm" :disabled="deletingId === p.id" @click="removeItem(p)">{{ deletingId === p.id ? "Deleting…" : "Delete" }}</button>
                <button type="button" class="pt-btn pt-btn-ghost pt-btn-sm" @click="confirmId = ''">Keep</button>
              </template>
              <button v-else type="button" class="pt-icon-btn danger" title="Delete" aria-label="Delete" @click="confirmId = p.id"><SiteIcon name="trash" :size="16" /></button>
            </span>
          </div>
        </div>
      </section>
    </div>

    <!-- Create / edit -->
    <div v-if="dlg" class="pt-overlay" @click.self="dlg = false">
      <div class="pt-modal" role="dialog" aria-modal="true">
        <div class="pt-modal-head">
          <h3>{{ form.id ? "Edit product" : "New product" }}</h3>
          <button type="button" class="pt-icon-btn" aria-label="Close" @click="dlg = false"><SiteIcon name="close" :size="18" /></button>
        </div>
        <div class="pt-modal-body pt-stack">
          <label class="pt-field">
            <span class="pt-label">Name</span>
            <input v-model="form.name" class="pt-input" placeholder="e.g. Parking tickets" @input="autoSlug" />
          </label>
          <label class="pt-field">
            <span class="pt-label">Web address <em>(slug, must be unique)</em></span>
            <span class="slug">
              <span class="slug-prefix">/products/</span>
              <input v-model="form.slug" class="pt-input" placeholder="parking-tickets" @input="slugTouched = true" @blur="form.slug = slugify(form.slug)" />
            </span>
            <span v-if="slugTaken" class="pt-hint bad">Another product already uses “{{ finalSlug }}”.</span>
          </label>
          <div class="pt-field">
            <span class="pt-label">Status</span>
            <div class="status-pick">
              <button v-for="s in statuses" :key="s.value" type="button" class="pt-btn pt-btn-sm" :class="form.status === s.value ? 'pt-btn-ink' : 'pt-btn-outline'" @click="form.status = s.value">{{ s.label }}</button>
            </div>
            <span class="pt-hint">{{ statuses.find((s) => s.value === form.status).hint }}</span>
          </div>
          <label class="pt-field">
            <span class="pt-label">Summary</span>
            <textarea v-model="form.summary" class="pt-textarea" rows="4" maxlength="400" placeholder="One or two sentences on what it does for a city's office and its residents."></textarea>
            <span class="pt-hint">{{ (form.summary || "").length }}/400</span>
          </label>
          <div v-if="saveError" class="pt-notice error"><SiteIcon name="alert" :size="17" /><span>{{ saveError }}</span></div>
        </div>
        <div class="pt-modal-foot">
          <button type="button" class="pt-btn pt-btn-ghost" @click="dlg = false">Cancel</button>
          <button type="button" class="pt-btn pt-btn-primary" :disabled="saving || !form.name.trim() || slugTaken" @click="save">{{ saving ? "Saving…" : form.id ? "Save changes" : "Create product" }}</button>
        </div>
      </div>
    </div>

    <div v-if="toastMsg" class="pt-toast" role="status">
      <span>{{ toastMsg }}</span>
      <button type="button" @click="toastMsg = ''">Close</button>
    </div>
  </div>
</template>

<script>
import "@/styles/portal.css";
import { db } from "@/firebase";
import { collection, addDoc, onSnapshot, orderBy, query, serverTimestamp, updateDoc, doc, deleteDoc } from "firebase/firestore";
import PortalTopBar from "@/components/PortalTopBar.vue";
import SiteIcon from "@/components/SiteIcon.vue";
import { initials, toDate } from "@/config/format";

// Starter products: the modules on the website. They're added as Paused so you can review them first.
const STARTERS = [
  { name: "Online payments", summary: "Residents pay tax, excise, water and sewer bills online by card or bank transfer, with receipts and daily deposit reports for the Treasurer's office." },
  { name: "Parking tickets", summary: "Officers issue tickets with a QR code, and drivers pay or request a hearing from their phone." },
  { name: "Report a problem (311)", summary: "Residents report potholes, streetlights and missed pickups with a photo and location. Departments get them as a work queue." },
  { name: "Permits & licenses", summary: "Apply, pay and get approved online for permits, dog licenses and business licenses." },
  { name: "Records requests", summary: "Residents request public records and certificates online, and staff track every request to completion." },
  { name: "Hearings", summary: "Schedule and hold parking and code hearings online, with decisions recorded on the case." },
  { name: "Emergency alerts", summary: "Send urgent notices to residents by app notification and email, with opt-outs handled for you." },
];

export default {
  name: "AdminProducts",
  components: { PortalTopBar, SiteIcon },
  data() {
    return {
      loading: true,
      loadError: "",
      products: [],
      search: "",
      statusFilter: "",
      statusTabs: [
        { value: "", label: "All" },
        { value: "active", label: "Active" },
        { value: "paused", label: "Paused" },
        { value: "archived", label: "Archived" },
      ],
      statuses: [
        { value: "active", label: "Active", hint: "Offered to cities now." },
        { value: "paused", label: "Paused", hint: "Hidden from your team's offer list, e.g. still being built." },
        { value: "archived", label: "Archived", hint: "No longer offered. Kept for records." },
      ],
      dlg: false,
      form: { id: "", name: "", slug: "", summary: "", status: "active" },
      slugTouched: false,
      saving: false,
      saveError: "",
      confirmId: "",
      deletingId: "",
      seeding: false,
      toastMsg: "",
    };
  },
  computed: {
    filtered() {
      const q = this.search.toLowerCase().trim();
      return this.products
        .filter((p) => !this.statusFilter || (p.status || "active") === this.statusFilter)
        .filter((p) => !q || [p.name, p.summary, p.slug].join(" ").toLowerCase().includes(q));
    },
    lastUpdated() {
      return this.products[0]?.updatedAt || null;
    },
    finalSlug() {
      return this.slugify(this.form.slug || this.form.name);
    },
    slugTaken() {
      return !!this.finalSlug && this.products.some((p) => p.id !== this.form.id && p.slug === this.finalSlug);
    },
    missingStarters() {
      const have = new Set(this.products.map((p) => this.slugify(p.slug || p.name)));
      return STARTERS.filter((s) => !have.has(this.slugify(s.name)));
    },
    hasWorkspace() {
      try {
        return this.$router.resolve({ name: "AdminProductDetail", params: { id: "x" } }).route.matched.length > 0;
      } catch (e) {
        return false;
      }
    },
  },
  created() {
    this.unsub = onSnapshot(
      query(collection(db, "products"), orderBy("updatedAt", "desc")),
      (snap) => {
        this.products = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        this.loading = false;
      },
      (e) => {
        console.error("Error loading products:", e);
        this.loadError = "Couldn't load products. Check that you're signed in as an admin.";
        this.loading = false;
      }
    );
  },
  beforeDestroy() {
    if (this.unsub) this.unsub();
  },
  methods: {
    initials,
    count(s) {
      return this.products.filter((p) => (p.status || "active") === s).length;
    },
    statusLabel(s) {
      return { active: "Active", paused: "Paused", archived: "Archived" }[s || "active"] || s;
    },
    statusTone(s) {
      return { active: "green", paused: "amber", archived: "outline" }[s || "active"] || "";
    },
    markTone(name = "") {
      const tones = ["ink", "green", "brass"];
      return tones[[...String(name)].reduce((a, c) => a + c.charCodeAt(0), 0) % tones.length];
    },
    prettyTs(ts) {
      const d = toDate(ts);
      return d ? d.toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—";
    },
    since(ts) {
      const d = toDate(ts);
      if (!d) return "";
      const s = Math.max(1, Math.floor((Date.now() - d.getTime()) / 1000));
      for (const [label, sec] of [["y", 31536000], ["mo", 2592000], ["d", 86400], ["h", 3600], ["m", 60]]) {
        const v = Math.floor(s / sec);
        if (v >= 1) return `${v}${label} ago`;
      }
      return "just now";
    },
    slugify(str = "") {
      return String(str).toLowerCase().replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "").slice(0, 64);
    },
    autoSlug() {
      if (!this.form.id && !this.slugTouched) this.form.slug = this.slugify(this.form.name);
    },
    toast(m) {
      this.toastMsg = m;
      clearTimeout(this._t);
      this._t = setTimeout(() => (this.toastMsg = ""), 3500);
    },
    openCreate() {
      this.form = { id: "", name: "", slug: "", summary: "", status: "active" };
      this.slugTouched = false;
      this.saveError = "";
      this.dlg = true;
    },
    edit(p) {
      this.form = { id: p.id, name: p.name || "", slug: p.slug || "", summary: p.summary || "", status: p.status || "active" };
      this.slugTouched = true;
      this.saveError = "";
      this.dlg = true;
    },
    openRow(p) {
      if (this.hasWorkspace) this.openWorkspace(p);
      else this.edit(p);
    },
    openWorkspace(p) {
      this.$router.push({ name: "AdminProductDetail", params: { id: p.id } }).catch(() => {});
    },
    async save() {
      if (this.slugTaken) return;
      this.saving = true;
      this.saveError = "";
      try {
        const payload = {
          name: this.form.name.trim(),
          slug: this.finalSlug,
          summary: (this.form.summary || "").trim(),
          status: this.form.status || "active",
          updatedAt: serverTimestamp(),
          visibility: "team",
        };
        if (this.form.id) await updateDoc(doc(db, "products", this.form.id), payload);
        else await addDoc(collection(db, "products"), { ...payload, createdAt: serverTimestamp() });
        this.dlg = false;
        this.toast(this.form.id ? "Product saved" : "Product created");
      } catch (e) {
        console.error(e);
        this.saveError = "Couldn't save. Please try again.";
      } finally {
        this.saving = false;
      }
    },
    async removeItem(p) {
      this.deletingId = p.id;
      try {
        await deleteDoc(doc(db, "products", p.id));
        this.toast(`Deleted “${p.name}”`);
      } catch (e) {
        console.error(e);
        this.toast("Couldn't delete it. Please try again.");
      } finally {
        this.deletingId = "";
        this.confirmId = "";
      }
    },
    async addStarters() {
      this.seeding = true;
      try {
        const list = this.missingStarters;
        for (const s of list) {
          await addDoc(collection(db, "products"), {
            name: s.name,
            slug: this.slugify(s.name),
            summary: s.summary,
            status: "paused",
            visibility: "team",
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          });
        }
        this.toast(`Added ${list.length} products as Paused. Review them, then set them Active.`);
      } catch (e) {
        console.error(e);
        this.toast("Couldn't add the starter products");
      } finally {
        this.seeding = false;
      }
    },
  },
};
</script>

<style scoped>
.head-actions,
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
}

.filters .pt-search {
  width: 240px;
}

.cols {
  grid-template-columns: minmax(0, 1fr) 110px 200px 170px;
  cursor: pointer;
}

.pt-thead.cols {
  cursor: default;
}

.name-cell {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.mark {
  width: 40px;
  height: 40px;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
}

.mark.ink { background: var(--ink); }
.mark.green { background: var(--green); }
.mark.brass { background: var(--brass); }

.name-text {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.name-text .pt-muted {
  font-size: 13px;
}

.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.updated {
  display: grid;
  gap: 2px;
  font-size: 13px;
  color: var(--ink-2);
}

.actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 6px;
  cursor: default;
}

.slug {
  display: flex;
  align-items: stretch;
}

.slug-prefix {
  display: flex;
  align-items: center;
  padding: 0 10px;
  border: 1.5px solid var(--rule-strong);
  border-right: 0;
  border-radius: 6px 0 0 6px;
  background: var(--paper-2);
  color: var(--muted);
  font-size: 13.5px;
  white-space: nowrap;
}

.mc-portal .slug .pt-input {
  border-radius: 0 6px 6px 0;
}

.status-pick {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.bad {
  color: var(--red-ink);
}

@media (max-width: 900px) {
  .cols {
    grid-template-columns: minmax(0, 1fr) auto;
  }
  .pt-thead.cols {
    display: none;
  }
  .cols > .updated {
    display: none;
  }
  .actions {
    grid-column: 1 / -1;
    justify-content: flex-start;
  }
}

@media (max-width: 600px) {
  .head-actions,
  .head-actions .pt-btn,
  .filters,
  .filters .pt-search {
    width: 100%;
  }
  .pt-tabs {
    width: 100%;
    overflow-x: auto;
  }
}
</style>